import { defineComponent as _defineComponent } from 'vue'
import { mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["alt"]

import { ReactiveHead, useHead } from '@unhead/vue'

import 'lazysizes'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components_new/BaseLink.vue'
import { LinkTarget } from '@/types'
import '@/utils/lazysizes-pg-thumbs-plugin'
import { processThumbSrcset } from '@/utils/pg-thumbs-helpers'

export type CardAppearance = 'big_horizontal' | 'medium_horizontal' | 'icon'

export type AppearanceMediaCondition = {
    media: string
    appearance: CardAppearance
}

interface CardContainerProps {
    is?: 'div' | typeof BaseLink
    to?: RouteLocationRaw
    appearance?: CardAppearance
    approximateWidth?: number
    target?: LinkTarget
    src?: string
    alt?: string
    srcsetOn?: boolean
    responsive?: boolean
    lazy?: boolean
    preload?: boolean
    /* appearance media and sources goes together and every element in array correspond each other */
    sources?: string[]
    appearanceMedia?: AppearanceMediaCondition[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CardContainer',
  props: {
    is: { default: 'div' },
    to: {},
    appearance: { default: 'medium_horizontal' },
    approximateWidth: {},
    target: {},
    src: {},
    alt: {},
    srcsetOn: { type: Boolean, default: true },
    responsive: { type: Boolean },
    lazy: { type: Boolean, default: true },
    preload: { type: Boolean },
    sources: {},
    appearanceMedia: {}
  },
  setup(__props: any) {

const props = __props

const conditionalAppearance = ref<CardAppearance | undefined>()
const conditionalSrc = ref<string | undefined>()
const effectiveAppearance = computed(() => conditionalAppearance.value || props.appearance)
const effectiveSrc = computed(() => conditionalSrc.value || props.src)
const mediaMatchers = ref<
    Array<{ matcher?: MediaQueryList | undefined; appearance: CardAppearance; src?: string | undefined }>
>([])

function updateAppearance() {
    mediaMatchers.value.forEach(({ matcher, appearance, src }) => {
        if (matcher?.matches) {
            conditionalAppearance.value = appearance
            conditionalSrc.value = src
        }
    })
}

function initMatchers() {
    removeMatchers()
    if (props.appearanceMedia?.length && props.sources?.length) {
        props.appearanceMedia.forEach((item, index) => {
            const matcher = window.matchMedia(item.media)
            mediaMatchers.value.push({ matcher, appearance: item.appearance, src: props.sources![index] })
            matcher.addEventListener('change', () => updateAppearance())
        })
        updateAppearance()
    }
}

function removeMatchers() {
    if (mediaMatchers.value.length) {
        mediaMatchers.value.forEach(({ matcher }) => {
            matcher?.removeEventListener('change', () => updateAppearance())
        })
        mediaMatchers.value = []
    }
}

onMounted(() => {
    initMatchers()
})

onBeforeUnmount(() => {
    removeMatchers()
})

watch(
    () => props.sources,
    () => {
        initMatchers()
    },
)

const srcSet = computed(() => {
    if (props.srcsetOn && !props.lazy && effectiveSrc.value) {
        let approximateWidth = 160

        if (props.appearance === 'icon') {
            approximateWidth = 90
        }
        if (props.appearance === 'big_horizontal') {
            approximateWidth = approximateWidth * 2
        }
        if (props.approximateWidth) {
            approximateWidth = props.approximateWidth
        }
        return processThumbSrcset(effectiveSrc.value, approximateWidth)
    }
    return ''
})

const preloadHeadLink = computed(() => {
    const link: ReactiveHead['link'] = []
    if (props.preload && effectiveSrc.value) {
        link.push({
            rel: 'preload',
            as: 'image',
            href: effectiveSrc.value,
            imagesrcset: srcSet.value,
            fetchpriority: 'high',
        })
    }
    return {
        link,
    }
})

useHead(preloadHeadLink)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(props.is), {
    to: props.to,
    target: props.target,
    class: _normalizeClass({
            [_ctx.$style.cardContainer]: true,
            [_ctx.$style.responsive]: props.responsive,
            [_ctx.$style.bigCard]: effectiveAppearance.value === 'big_horizontal',
            [_ctx.$style.icon]: effectiveAppearance.value === 'icon',
        })
  }, {
    default: _withCtx(() => [
      (effectiveSrc.value)
        ? (_openBlock(), _createElementBlock("img", _mergeProps({
            key: 
                props.lazy
                    ? `${effectiveSrc.value}_${effectiveAppearance.value}`
                    : undefined /* remount the image if it is lazy, otherwise nobody update src */
            ,
            "data-allow-mismatch": "",
            alt: props.alt
          }, 
                props.lazy
                    ? {
                          'data-src': effectiveSrc.value,
                          'data-sizes': 'auto',
                          class: [_ctx.$style.img, 'lazyload'],
                          loading: 'lazy',
                      }
                    : {
                          src: effectiveSrc.value,
                          class: _ctx.$style.img,
                          fetchpriority: 'high',
                          srcset: srcSet.value,
                      }
            , { itemprop: "image" }), null, 16, _hoisted_1))
        : _createCommentVNode("", true),
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 3
  }, 8, ["to", "target", "class"]))
}
}

})